import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { MenuState } from './menu-state';
import { MenuItemEntity, AuthRole } from '@gishub/domain';
import { MenuRepositoryProvider } from '../../../../shared/providers/repository-providers';
import { AuthProvider } from '../../../auth/shared/auth-provider';

/**
 * Pure function: filtra ítems del menú según autenticación y roles.
 *
 * Reglas de negocio:
 * - Si NO autenticado → solo ítems con isPublic = true
 * - Si autenticado → ítems públicos (+) ítems cuyo `code` coincida con algún `alias` en roles[].options[].alias
 */
function filterMenuItems(
  items: MenuItemEntity[],
  roles: AuthRole[],
  isAuthenticated: boolean,
): MenuItemEntity[] {
  if (!isAuthenticated) {
    return items.filter((i) => i.isPublic);
  }

  const allowedAliases = new Set(roles.flatMap((r) => r.options.map((o) => o.alias)));
  return items.filter((i) => i.isPublic || allowedAliases.has(i.code));
}

@Injectable()
export class MenuNotifier extends BaseNotifier<MenuState> {
  private static readonly INITIAL: MenuState = {
    activeId: null,
    items: [],
  };

  /** Full unfiltered menu items loaded from repository */
  private allItems: MenuItemEntity[] = [];

  constructor(ref: Ref) {
    super(ref, MenuNotifier.INITIAL);
  }

  override build(): void {
    this.setState(MenuNotifier.INITIAL);

    // 1. Load menu items from repository
    this.ref
      .watch(MenuRepositoryProvider)
      .instance.getItems()
      .then((res) => {
        if (res.success && res.data) {
          this.allItems = res.data!;
          this.applyFilter();
        }
      })
      .catch((err) => {
        console.error('Failed to load menu items:', err);
      });

    // 2. Reactively re-apply filter when auth state changes (login/logout)
    this.listen(() => {
      // Track auth signal changes
      this.ref.watch(AuthProvider).state();
      // Re-apply filter if items are already loaded
      if (this.allItems.length > 0) {
        this.applyFilter();
      }
    });
  }

  /** Re-filter items based on current auth state */
  private applyFilter(): void {
    const authState = this.ref.read(AuthProvider).state();
    const filtered = filterMenuItems(this.allItems, authState.roles, authState.isAuthenticated);
    this.updateState((s) => ({ ...s, items: filtered }));
  }

  setActive(id: string): void {
    const current = this.snapshot().activeId;
    this.setState({ ...this.snapshot(), activeId: current === id ? null : id });
  }

  get activeItem(): MenuItemEntity | undefined {
    const s = this.snapshot();
    return s.items.find((i) => i.id === s.activeId);
  }
}